<template>
	<div class="subscription-page">
		<div class="subscription-actions" v-if="subscriptionsComputed.length">
			<button type="button" class="manage-subscriptions" @click="manageSubscriptions">
				<portal-icon name="settings" :size="17"></portal-icon>
				{{ $t('subscriptions.manage') }}
			</button>
		</div>

		<div class="subscription-table-shell portal-compact-list-shell">
			<v-data-table
				item-key="id"
				class="portal-compact-table subscription-table"
				:headers="headers"
				:items="subscriptionsComputed"
				:hide-default-footer="true"
				:items-per-page="-1"
				:mobile-breakpoint="0"
				@click:row="handleItemClick($event)"
			>
			<template v-slot:item.periodStart="{ item }">
				<div class="subscription-period">
					<portal-icon name="calendar-days" :size="16"></portal-icon>
					<span v-if="item.periodStart && item.periodEnd">
						{{ DateTime.fromISO(item.periodStart).toLocaleString() }} -
						{{ DateTime.fromISO(item.periodEnd).toLocaleString() }}
					</span>
					<span v-else>--</span>
				</div>
			</template>

			<template v-slot:item.daysUntilDue="{ item }">
				<span class="portal-cell-muted">{{ item.daysUntilDue || '--' }}</span>
			</template>

			<template v-slot:item.subscriptionItems="{ item }">
				<div class="subscription-items">
					<div v-for="(subItem, index) in item.subscriptionItems" :key="item.id + ':' + index">
						{{ subItem.quantity }} x {{ subItem.description }} @
						{{ $formatters.dollars(subItem.amount, true, true, subItem.currency) }}
					</div>
				</div>
			</template>

			<template v-slot:item.status="{ item }">
				<portal-status-pill :background="statusDetail(item).background" :color="statusDetail(item).color">
					{{ statusDetail(item).label }}
				</portal-status-pill>
			</template>

				<template v-slot:item.action>
					<portal-icon class="row-chevron" name="chevron-right" :size="17"></portal-icon>
				</template>
			</v-data-table>
		</div>
	</div>
</template>

<script>
	import SubscriptionService from '@/modules/subscriptions/SubscriptionService';
	import { DateTime } from 'luxon';
  import SubscriptionPayments from '@/modules/subscriptions/SubscriptionPayments';
	import PortalIcon from '@/components/PortalIcon.vue';
	import PortalStatusPill from '@/components/PortalStatusPill.vue';

	export default {
		name: 'SubscriptionList',

		props: [],

		components: { PortalIcon, PortalStatusPill },

		data: function() {
			return {
				subscriptionService: new SubscriptionService(),
				subscriptions: [],
				requests: [],
				DateTime: DateTime,
			};
		},

		mounted() {
			this.getSubscriptions();
			this.getSubscriptionRequests();
		},

		beforeDestroy() {},

		methods: {
			statusDetail(item) {
				if (item.status === 'active' && item.cancelAtPeriodEnd) {
					return {
						label: this.$t('subscriptions.cancellation'),
						background: 'var(--v-warning_10-base)',
						color: 'var(--v-warning-base)',
					};
				}
				if (item.status === 'active') {
					return {
						label: item.status,
						background: 'var(--v-success_10-base)',
						color: 'var(--v-success-base)',
					};
				}
				if (item.status === 'sent') {
					return {
						label: item.status,
						background: 'color-mix(in srgb, var(--v-primary-base) 10%, var(--portal-surface))',
						color: 'var(--v-primary-base)',
					};
				}
				return {
					label: item.status.replace('_', ' '),
					background: 'var(--v-gray_20-base)',
					color: 'var(--portal-muted)',
				};
			},

			getSubscriptions: function() {
				this.subscriptionService.getSubscriptions().then((res) => {
					this.subscriptions.splice(0, this.subscriptions.length);
					this.subscriptions.push(...res.data);
				});
			},

			getSubscriptionRequests: function() {
				this.subscriptionService.getSubscriptionRequests().then((res) => {
					this.requests.splice(0, this.requests.length);
					this.requests.push(...res.data);
				});
			},

      handleItemClick: function(subscription){
        if(subscription.status === 'sent'){
          document.location.href = subscription.checkoutUrl;
        }else{
          this.viewPayments(subscription);
        }
      },

      viewPayments: function(subscription){
        let binding = {
          subscription: subscription,
        };

        this.$store.state.globalModalController.openModal(SubscriptionPayments,binding);
      },

      manageSubscriptions: function(){
        this.subscriptionService.getSubscriptionPortalLink(document.location.href).then((res) => {
          document.location.href = res.data;
        })
      }
		},

		computed: {
			subscriptionsComputed: function() {
				let result = [...this.subscriptions];

				for (let i = 0; i < this.requests.length; i++) {
					let request = this.requests[i];

					let pendingSubscription = {
						id: request.id,
						clientMini: {},
						periodStart: null,
						periodEnd: null,
						daysUntilDue: null,
						subscriptionItems: [],
						checkoutUrl: request.checkoutUrl,
						status: 'sent',
					};

          for(let j=0; j < request.productSelections.length; j++){
            let product = request.productSelections[j];
            pendingSubscription.subscriptionItems.push({
              quantity: product.quantity,
              description: product.subscriptionProduct.name,
              amount: product.subscriptionProduct.prices[0].unitAmount / 100,
              currency: product.subscriptionProduct.prices[0].currency,
            });
          }

					result.push(pendingSubscription);
				}
				return result;
			},

			headers: function() {
				return [
					{ text: this.$t('stripe.subscription.period'), value: 'periodStart' },
					{ text: this.$t('stripe.subscription.days-until'), value: 'daysUntilDue' },
					{ text: this.$t('stripe.subscription.items'), value: 'subscriptionItems' },
					{ text: this.$t('stripe.subscription.status'), value: 'status' },
					{ text: '', value: 'action', align: 'right' },
				];
			},
		},
	};
</script>

<style scoped lang="scss">
	.subscription-page {
		display: grid;
		gap: 14px;
		padding: 16px;
	}

	.subscription-actions {
		display: flex;
		justify-content: flex-end;
	}

	.manage-subscriptions {
		min-height: 42px;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 0 14px;
		border: 1px solid var(--v-primary-base);
		border-radius: var(--brand-button-radius, 11px);
		background: var(--v-primary-base);
		color: var(--v-white-base);
		font: inherit;
		font-size: 13px;
		font-weight: 650;
		cursor: pointer;
	}

	.subscription-table-shell {
		min-width: 0;
		overflow: hidden;
		border-radius: var(--portal-radius-md);
	}

	.subscription-table ::v-deep tbody tr {
		cursor: pointer;
	}

	.subscription-period {
		min-width: 0;
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.subscription-period svg {
		flex: 0 0 auto;
		color: var(--portal-muted);
	}

	.subscription-period span,
	.subscription-items div {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.subscription-items {
		min-width: 0;
		display: grid;
		gap: 2px;
	}

	.row-chevron {
		color: var(--portal-muted);
	}

	@container portal-compact-list (max-width: 760px) {
		.subscription-table ::v-deep th:nth-child(2),
		.subscription-table ::v-deep td:nth-child(2) {
			display: none;
		}
	}

	@container portal-compact-list (max-width: 580px) {
		.subscription-table ::v-deep th:nth-child(3),
		.subscription-table ::v-deep td:nth-child(3) {
			display: none;
		}
	}

	@media (max-width: 599px) {
		.subscription-page {
			padding: 12px;
		}

		.subscription-table ::v-deep th:nth-child(2),
		.subscription-table ::v-deep td:nth-child(2),
		.subscription-table ::v-deep th:nth-child(3),
		.subscription-table ::v-deep td:nth-child(3) {
			display: none;
		}

		.subscription-table ::v-deep table {
			min-width: 0 !important;
			width: 100% !important;
		}

		.manage-subscriptions {
			width: 100%;
			justify-content: center;
		}
	}
</style>
